import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { BreadcrumbProvider, Breadcrumbs, useAuth } from '@monecolier/ui';
import { getMe, type MembershipRole } from '@monecolier/api-client';
import { breadcrumbRoutes } from '../breadcrumbs';
import { canAccessRoute } from '../lib/roles';

const NO_ROUTES: never[] = [];

/**
 * UX-85 — fil d'Ariane de web-admin, monté UNE fois autour de `<Routes>`
 * (App.tsx). Branche `ROUTE_ACCESS` (lib/roles.ts) comme `canNavigate` : quand
 * une page a plusieurs fils possibles et que l'origine est inconnue (lien
 * profond), le fil retenu est le premier dont tous les maillons sont
 * accessibles au compte — un TEACHER arrivé par favori sur l'emploi du temps
 * d'une classe voit « Emploi du temps », jamais « Gestion pédagogique » qui le
 * renverrait vers l'accueil.
 *
 * Aucun fil tant que les rôles ne sont pas connus (même règle que
 * `RequireRouteAccess`, qui ne rend pas la page avant) : afficher d'abord un
 * fil puis basculer sur l'autre serait un saut visible.
 */
export function AppBreadcrumbs({ children }: { children: ReactNode }) {
  const { accessToken, apiClient, isAuthenticated } = useAuth();
  // `undefined` = pas encore résolu ; `null` = échec (tout est considéré accessible).
  const [roles, setRoles] = useState<MembershipRole[] | null | undefined>(undefined);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setRoles(undefined);
      return;
    }
    let cancelled = false;
    getMe(apiClient, accessToken)
      .then((me) => {
        if (!cancelled) setRoles(me.roles ?? []);
      })
      .catch(() => {
        if (!cancelled) setRoles(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken, apiClient]);

  const canNavigate = useCallback((to: string) => (roles ? canAccessRoute(roles, to) : true), [roles]);

  return (
    <BreadcrumbProvider routes={roles === undefined ? NO_ROUTES : breadcrumbRoutes} canNavigate={canNavigate}>
      <Breadcrumbs />
      {children}
    </BreadcrumbProvider>
  );
}
